/**
 * Notification settings API client (BFF: `/api/v1/store/settings/notifications`).
 *
 * Covers the notification settings document plus notification-provider CRUD and
 * connection testing. Provider secrets are write-only on upsert and never returned.
 */
import { apiClient } from '@/shared/api';
import type {
  NotificationSettings,
  ProviderSummary,
  ProviderUpsertRequest,
  TestConnectionResult,
  UpdateNotificationSettingsRequest,
} from '../model/settingsTypes';

const BASE = '/api/v1/store/settings/notifications';

export const getNotificationSettings = async (): Promise<NotificationSettings> =>
  (await apiClient.get<NotificationSettings>(BASE)).data;

export const updateNotificationSettings = async (
  body: UpdateNotificationSettingsRequest,
): Promise<NotificationSettings> => (await apiClient.put<NotificationSettings>(BASE, body)).data;

export const listNotificationProviders = async (): Promise<ProviderSummary[]> =>
  (await apiClient.get<ProviderSummary[]>(`${BASE}/providers`)).data;

export const createNotificationProvider = async (
  body: ProviderUpsertRequest,
): Promise<ProviderSummary> =>
  (await apiClient.post<ProviderSummary>(`${BASE}/providers`, body)).data;

export const updateNotificationProvider = async (
  providerId: string,
  body: ProviderUpsertRequest,
): Promise<ProviderSummary> =>
  (await apiClient.put<ProviderSummary>(`${BASE}/providers/${providerId}`, body)).data;

export const deleteNotificationProvider = async (providerId: string): Promise<void> => {
  await apiClient.delete(`${BASE}/providers/${providerId}`);
};

export const testNotificationProviderConnection = async (
  providerId: string,
): Promise<TestConnectionResult> =>
  (await apiClient.post<TestConnectionResult>(`${BASE}/providers/${providerId}/test-connection`))
    .data;
